import { type AdminUserDto } from '../../services/admin.api';
import { getEuCountryChoices } from '../../services/eu-countries';
import { t } from '../../services/i18n';
import { esc, escAttr, renderBooleanOptions, renderRow, type AdminBooleanFilter } from './admin-shared';

export class RcAdminUsersView extends HTMLElement {
  users: AdminUserDto[] = [];
  allowedCountryCodes: string[] = [];
  advertisingEnabled = true;
  googleLoginEnabled = true;
  microsoftLoginEnabled = true;
  newUserAccessEnabled = true;
  newSubscriptionAccessEnabled = true;
  isSavingAdvertising = false;
  isSavingAllowedCountries = false;
  isSavingGoogleLogin = false;
  isSavingMicrosoftLogin = false;
  isSavingNewUserAccess = false;
  isSavingNewSubscriptionAccess = false;
  advertisingError = '';
  allowedCountriesError = '';
  googleLoginError = '';
  microsoftLoginError = '';
  newUserAccessError = '';
  newSubscriptionAccessError = '';
  totalCount = 0;
  page = 1;
  pageSize = 25;
  totalPages = 0;
  selectedUserIds: string[] = [];
  filters = {
    name: '',
    email: '',
    id: '',
    business: '' as AdminBooleanFilter,
    onboarding: '' as AdminBooleanFilter,
  };

  render(): void {
    const selectedUserIds = new Set(this.selectedUserIds);
    const allowedCountryCodeSet = new Set(this.allowedCountryCodes);
    const selectableUsers = this.users.filter((user) => user.hasManageableSubscription);
    const allVisibleSelectableSelected = selectableUsers.length > 0 && selectableUsers.every((user) => selectedUserIds.has(user.id));
    const countryChoices = getEuCountryChoices();

    this.innerHTML = `
      <div class="card">
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.allowedCountries.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.allowedCountries.sectionTitle')}</strong>
            <p>${t('admin.allowedCountries.sectionDescription')}</p>
            <div class="admin-country-grid">
              ${countryChoices.map((country) => `
                <label class="admin-country-option">
                  <input
                    type="checkbox"
                    value="${country.code}"
                    data-action="allowed-country"
                    ${allowedCountryCodeSet.has(country.code) ? 'checked' : ''}
                    ${this.isSavingAllowedCountries ? 'disabled' : ''}
                  >
                  <span>${esc(country.label)}</span>
                </label>
              `).join('')}
            </div>
            ${this.allowedCountriesError ? `<p class="admin-new-user-access-error">${esc(this.allowedCountriesError)}</p>` : ''}
          </div>
          <div class="admin-country-actions">
            <button type="button" class="btn btn-primary" data-action="save-allowed-countries" ${this.isSavingAllowedCountries ? 'disabled' : ''}>${this.isSavingAllowedCountries ? t('common.saving') : t('admin.allowedCountries.saveButton')}</button>
          </div>
        </section>
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.advertising.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.advertising.sectionTitle')}</strong>
            <p>${t('admin.advertising.sectionDescription')}</p>
            <div class="admin-new-user-access-status ${this.advertisingEnabled ? 'is-enabled' : 'is-disabled'}">
              ${this.advertisingEnabled ? t('admin.advertising.enabled') : t('admin.advertising.disabled')}
            </div>
            ${this.advertisingError ? `<p class="admin-new-user-access-error">${esc(this.advertisingError)}</p>` : ''}
          </div>
          <label class="admin-new-user-access-toggle">
            <input
              class="admin-new-user-access-checkbox"
              type="checkbox"
              data-action="toggle-advertising"
              ${this.advertisingEnabled ? 'checked' : ''}
              ${this.isSavingAdvertising ? 'disabled' : ''}
            >
            <span class="admin-new-user-access-switch" aria-hidden="true"></span>
            <span class="admin-new-user-access-toggle-label">${this.isSavingAdvertising ? t('common.loading') : t('admin.advertising.toggleLabel')}</span>
          </label>
        </section>
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.googleLogin.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.googleLogin.sectionTitle')}</strong>
            <p>${t('admin.googleLogin.sectionDescription')}</p>
            <div class="admin-new-user-access-status ${this.googleLoginEnabled ? 'is-enabled' : 'is-disabled'}">
              ${this.googleLoginEnabled ? t('admin.googleLogin.enabled') : t('admin.googleLogin.disabled')}
            </div>
            ${this.googleLoginError ? `<p class="admin-new-user-access-error">${esc(this.googleLoginError)}</p>` : ''}
          </div>
          <label class="admin-new-user-access-toggle">
            <input
              class="admin-new-user-access-checkbox"
              type="checkbox"
              data-action="toggle-google-login"
              ${this.googleLoginEnabled ? 'checked' : ''}
              ${this.isSavingGoogleLogin ? 'disabled' : ''}
            >
            <span class="admin-new-user-access-switch" aria-hidden="true"></span>
            <span class="admin-new-user-access-toggle-label">${this.isSavingGoogleLogin ? t('common.loading') : t('admin.googleLogin.toggleLabel')}</span>
          </label>
        </section>
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.microsoftLogin.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.microsoftLogin.sectionTitle')}</strong>
            <p>${t('admin.microsoftLogin.sectionDescription')}</p>
            <div class="admin-new-user-access-status ${this.microsoftLoginEnabled ? 'is-enabled' : 'is-disabled'}">
              ${this.microsoftLoginEnabled ? t('admin.microsoftLogin.enabled') : t('admin.microsoftLogin.disabled')}
            </div>
            ${this.microsoftLoginError ? `<p class="admin-new-user-access-error">${esc(this.microsoftLoginError)}</p>` : ''}
          </div>
          <label class="admin-new-user-access-toggle">
            <input
              class="admin-new-user-access-checkbox"
              type="checkbox"
              data-action="toggle-microsoft-login"
              ${this.microsoftLoginEnabled ? 'checked' : ''}
              ${this.isSavingMicrosoftLogin ? 'disabled' : ''}
            >
            <span class="admin-new-user-access-switch" aria-hidden="true"></span>
            <span class="admin-new-user-access-toggle-label">${this.isSavingMicrosoftLogin ? t('common.loading') : t('admin.microsoftLogin.toggleLabel')}</span>
          </label>
        </section>
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.newUserAccess.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.newUserAccess.sectionTitle')}</strong>
            <p>${t('admin.newUserAccess.sectionDescription')}</p>
            <div class="admin-new-user-access-status ${this.newUserAccessEnabled ? 'is-enabled' : 'is-disabled'}">
              ${this.newUserAccessEnabled ? t('admin.newUserAccess.enabled') : t('admin.newUserAccess.disabled')}
            </div>
            ${this.newUserAccessError ? `<p class="admin-new-user-access-error">${esc(this.newUserAccessError)}</p>` : ''}
          </div>
          <label class="admin-new-user-access-toggle">
            <input
              class="admin-new-user-access-checkbox"
              type="checkbox"
              data-action="toggle-new-user-access"
              ${this.newUserAccessEnabled ? 'checked' : ''}
              ${this.isSavingNewUserAccess ? 'disabled' : ''}
            >
            <span class="admin-new-user-access-switch" aria-hidden="true"></span>
            <span class="admin-new-user-access-toggle-label">${this.isSavingNewUserAccess ? t('common.loading') : t('admin.newUserAccess.toggleLabel')}</span>
          </label>
        </section>
        <section class="admin-new-user-access" aria-label="${escAttr(t('admin.newSubscriptionAccess.sectionTitle'))}">
          <div class="admin-new-user-access-copy">
            <strong>${t('admin.newSubscriptionAccess.sectionTitle')}</strong>
            <p>${t('admin.newSubscriptionAccess.sectionDescription')}</p>
            <div class="admin-new-user-access-status ${this.newSubscriptionAccessEnabled ? 'is-enabled' : 'is-disabled'}">
              ${this.newSubscriptionAccessEnabled ? t('admin.newSubscriptionAccess.enabled') : t('admin.newSubscriptionAccess.disabled')}
            </div>
            ${this.newSubscriptionAccessError ? `<p class="admin-new-user-access-error">${esc(this.newSubscriptionAccessError)}</p>` : ''}
          </div>
          <label class="admin-new-user-access-toggle">
            <input
              class="admin-new-user-access-checkbox"
              type="checkbox"
              data-action="toggle-new-subscription-access"
              ${this.newSubscriptionAccessEnabled ? 'checked' : ''}
              ${this.isSavingNewSubscriptionAccess ? 'disabled' : ''}
            >
            <span class="admin-new-user-access-switch" aria-hidden="true"></span>
            <span class="admin-new-user-access-toggle-label">${this.isSavingNewSubscriptionAccess ? t('common.loading') : t('admin.newSubscriptionAccess.toggleLabel')}</span>
          </label>
        </section>
        <form class="admin-users-filters">
          <div class="form-row">
            <div class="form-group">
              <label for="admin-filter-name">${t('admin.filter.name')}</label>
              <input id="admin-filter-name" name="name" type="text" value="${escAttr(this.filters.name)}" placeholder="${t('admin.filter.namePlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-filter-email">${t('admin.filter.email')}</label>
              <input id="admin-filter-email" name="email" type="text" value="${escAttr(this.filters.email)}" placeholder="${t('admin.filter.emailPlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-filter-id">${t('admin.filter.id')}</label>
              <input id="admin-filter-id" name="id" type="text" value="${escAttr(this.filters.id)}" placeholder="${t('admin.filter.idPlaceholder')}" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="admin-filter-business">${t('admin.filter.business')}</label>
              <select id="admin-filter-business" name="business">${renderBooleanOptions(this.filters.business, 'business')}</select>
            </div>
            <div class="form-group">
              <label for="admin-filter-onboarding">${t('admin.filter.onboarding')}</label>
              <select id="admin-filter-onboarding" name="onboarding">${renderBooleanOptions(this.filters.onboarding, 'onboarding')}</select>
            </div>
            <div class="admin-users-filter-actions">
              <button type="submit" class="btn btn-primary">${t('admin.filter.apply')}</button>
              <button type="button" class="btn btn-outline" data-action="reset-filters">${t('admin.filter.reset')}</button>
            </div>
          </div>
        </form>
        <div class="admin-users-toolbar">
          <div class="admin-users-toolbar-main">
            <div class="admin-users-meta">${t('admin.userCount', { count: this.totalCount })}</div>
            <div class="admin-pagination-info">${this.renderPaginationSummary()}</div>
          </div>
          <div class="admin-users-toolbar-actions">
            <button type="button" class="btn btn-danger" data-action="open-force-cancel-selected" ${selectedUserIds.size ? '' : 'disabled'}>${t('admin.bulkSubscription.cancelSelected')}</button>
            <button type="button" class="btn btn-outline" data-action="open-force-cancel-all-paid">${t('admin.bulkSubscription.cancelAllPaid')}</button>
            <button type="button" class="btn btn-danger" data-action="open-platform-reset">${t('admin.platformReset.openButton')}</button>
          </div>
        </div>
        <div class="table-wrapper">
          <table class="admin-table">
            <thead>
              <tr>
                <th class="admin-col-select">
                  <input type="checkbox" data-action="toggle-select-all-users" ${allVisibleSelectableSelected ? 'checked' : ''} ${selectableUsers.length ? '' : 'disabled'}>
                </th>
                <th class="admin-col-provider">${t('admin.col.provider')}</th>
                <th class="admin-col-name">${t('admin.col.effectiveDisplayName')}</th>
                <th class="admin-col-email">${t('admin.col.email')}</th>
                <th class="admin-col-date">${t('admin.col.createdAt')}</th>
                <th class="admin-col-date">${t('admin.col.lastLoggedIn')}</th>
                <th class="admin-col-role">${t('admin.col.blocked')}</th>
                <th class="admin-col-flag">${t('admin.col.onboarding')}</th>
                <th class="admin-col-flag">${t('admin.col.business')}</th>
                <th class="admin-col-role">${t('admin.col.role')}</th>
                <th class="admin-col-action"></th>
              </tr>
            </thead>
            <tbody>
              ${this.users.length
                ? this.users.map((user, index) => renderRow(user, index, selectedUserIds.has(user.id), user.hasManageableSubscription === true)).join('')
                : `<tr><td colspan="11" class="empty-state admin-users-empty">${t('admin.noResults')}</td></tr>`}
            </tbody>
          </table>
        </div>
        <div class="admin-pagination">
          <div class="admin-pagination-info">${t('admin.pagination.page', {
            page: this.totalPages === 0 ? 0 : this.page,
            totalPages: this.totalPages,
          })}</div>
          <div class="admin-pagination-actions">
            <button type="button" class="btn btn-outline" data-page="${this.page - 1}" ${this.page <= 1 ? 'disabled' : ''}>${t('admin.pagination.previous')}</button>
            <button type="button" class="btn btn-outline" data-page="${this.page + 1}" ${this.page >= this.totalPages ? 'disabled' : ''}>${t('admin.pagination.next')}</button>
          </div>
        </div>
      </div>
    `;

    this.querySelector<HTMLFormElement>('.admin-users-filters')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('admin-users-apply-filters', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });
    this.querySelector('[data-action="reset-filters"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('admin-users-reset-filters', { bubbles: true }));
    });
    this.querySelector('[data-action="open-platform-reset"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('admin-users-open-platform-reset', { bubbles: true }));
    });
    this.querySelector('[data-action="open-force-cancel-selected"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('admin-users-open-force-cancel-selected', { bubbles: true }));
    });
    this.querySelector('[data-action="open-force-cancel-all-paid"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('admin-users-open-force-cancel-all-paid', { bubbles: true }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-advertising"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-advertising', {
        bubbles: true,
        detail: { isEnabled: input.checked },
      }));
    });
    this.querySelector('[data-action="save-allowed-countries"]')?.addEventListener('click', () => {
      const countryCodes = Array.from(this.querySelectorAll<HTMLInputElement>('[data-action="allowed-country"]:checked'))
        .map((input) => input.value);
      this.dispatchEvent(new CustomEvent('admin-users-save-allowed-countries', {
        bubbles: true,
        detail: { countryCodes },
      }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-google-login"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-google-login', {
        bubbles: true,
        detail: { isEnabled: input.checked },
      }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-microsoft-login"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-microsoft-login', {
        bubbles: true,
        detail: { isEnabled: input.checked },
      }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-new-user-access"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-new-user-access', {
        bubbles: true,
        detail: { isEnabled: input.checked },
      }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-new-subscription-access"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-new-subscription-access', {
        bubbles: true,
        detail: { isEnabled: input.checked },
      }));
    });
    this.querySelector<HTMLInputElement>('[data-action="toggle-select-all-users"]')?.addEventListener('change', (event) => {
      const input = event.currentTarget as HTMLInputElement;
      this.dispatchEvent(new CustomEvent('admin-users-toggle-select-all-users', {
        bubbles: true,
        detail: { isSelected: input.checked },
      }));
    });
    this.querySelectorAll<HTMLInputElement>('[data-action="toggle-user-selection"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.dispatchEvent(new CustomEvent('admin-users-toggle-user-selection', {
          bubbles: true,
          detail: { userId: input.dataset.userId, isSelected: input.checked },
        }));
      });
    });
    this.querySelectorAll<HTMLButtonElement>('.admin-eye-btn').forEach((button) => {
      button.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('admin-users-open-details', {
          bubbles: true,
          detail: { index: Number(button.dataset.index) },
        }));
      });
    });
    this.querySelectorAll<HTMLButtonElement>('[data-page]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.dispatchEvent(new CustomEvent('admin-users-page', {
          bubbles: true,
          detail: { page: Number(button.dataset.page) },
        }));
      });
    });
  }

  private renderPaginationSummary(): string {
    if (this.totalCount === 0) {
      return t('admin.pagination.noResults');
    }
    const start = (this.page - 1) * this.pageSize + 1;
    const end = start + this.users.length - 1;
    return t('admin.pagination.summary', { start, end, total: this.totalCount });
  }
}

if (!customElements.get('rc-admin-users-view')) {
  customElements.define('rc-admin-users-view', RcAdminUsersView);
}
